import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { reportPdfStyles as styles } from '../../../Styles/reportPdfStyles';

interface SupportReportData {
  dateRange: string;
  kpis: {
    totalTasks: number;
    completedTasks: number;
    completionRate: number;
    averageResponseTime: number;
  };
  tasksByStatus?: Array<{ status: string; count: number; percentage: number; }>;
  tasksByPriority?: Array<{ priority: string; count: number; percentage: number; }>;
  ticketsByStatus?: Array<{ status: string; count: number; percentage: number; }>;
  ticketsByType?: Array<{ type: string; count: number; percentage: number; }>;
  ticketVolume?: Array<{ date: string; count: number; }>;
  responseTimesByType?: Array<{ type: string; avgResponseTime: number; }>;
  agentWorkload?: Array<{ agent: string; tasks: number; tickets: number; }>;
  agentCompletionRate?: Array<{ agent: string; completionRate: number; }>;
}

interface SupportPDFReportProps {
  data: SupportReportData;
}

const SupportPDFReport: React.FC<SupportPDFReportProps> = ({ data }) => {
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('ro-RO').format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  const formatTime = (hours: number) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)} min`;
    }
    return `${hours.toFixed(1)} h`;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.title}>Support Report</Text>
          <Text style={styles.subtitle}>Cafe-Mag CRM</Text>
          <Text style={styles.dateRange}>Period: {data.dateRange}</Text>
        </View>

        {/* KPIs */}
        <View style={styles.kpiSection}>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Total Tasks</Text>
            <Text style={styles.kpiValue}>{formatNumber(data.kpis.totalTasks)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Completed Tasks</Text>
            <Text style={styles.kpiValue}>{formatNumber(data.kpis.completedTasks)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Completion Rate</Text>
            <Text style={styles.kpiValue}>{formatPercentage(data.kpis.completionRate)}</Text>
          </View>
          <View style={styles.kpiCard}>
            <Text style={styles.kpiLabel}>Avg Response Time</Text>
            <Text style={styles.kpiValue}>{formatTime(data.kpis.averageResponseTime)}</Text>
          </View>
        </View>

        {/* Tasks by Status */}
        {data.tasksByStatus && data.tasksByStatus.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Tasks by Status</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Status</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Percentage</Text>
              </View>
              {data.tasksByStatus.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.status}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.percentage)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Tasks by Priority */}
        {data.tasksByPriority && data.tasksByPriority.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Tasks by Priority</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Priority</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Percentage</Text>
              </View>
              {data.tasksByPriority.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.priority}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.percentage)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Tickets by Status */}
        {data.ticketsByStatus && data.ticketsByStatus.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Tickets by Status</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Status</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Percentage</Text>
              </View>
              {data.ticketsByStatus.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.status}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.percentage)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Tickets by Type */}
        {data.ticketsByType && data.ticketsByType.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Tickets by Type</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Type</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
                <Text style={styles.tableCellHeader}>Percentage</Text>
              </View>
              {data.ticketsByType.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.type}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.percentage)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Ticket Volume Over Time */}
        {data.ticketVolume && data.ticketVolume.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Ticket Volume Over Time</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Date</Text>
                <Text style={styles.tableCellHeader}>Count</Text>
              </View>
              {data.ticketVolume.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.date}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.count)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Response Times by Type */}
        {data.responseTimesByType && data.responseTimesByType.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Response Times by Type</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Type</Text>
                <Text style={styles.tableCellHeader}>Avg Response Time</Text>
              </View>
              {data.responseTimesByType.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.type}</Text>
                  <Text style={styles.tableCell}>{formatTime(item.avgResponseTime)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Agent Workload */}
        {data.agentWorkload && data.agentWorkload.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Agent Workload</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Agent</Text>
                <Text style={styles.tableCellHeader}>Tasks</Text>
                <Text style={styles.tableCellHeader}>Tickets</Text>
              </View>
              {data.agentWorkload.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.agent}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.tasks)}</Text>
                  <Text style={styles.tableCell}>{formatNumber(item.tickets)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Agent Completion Rate */}
        {data.agentCompletionRate && data.agentCompletionRate.length > 0 && (
          <View>
            <Text style={styles.sectionTitle}>Agent Completion Rate</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={styles.tableCellHeader}>Agent</Text>
                <Text style={styles.tableCellHeader}>Completion Rate</Text>
              </View>
              {data.agentCompletionRate.map((item, index) => (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.tableCell}>{item.agent}</Text>
                  <Text style={styles.tableCell}>{formatPercentage(item.completionRate)}</Text>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* Footer */}
        <Text style={styles.footer} fixed>
          Generated by Cafe-Mag CRM - {new Date().toLocaleDateString('ro-RO')}
        </Text>
      </Page>
    </Document>
  );
};

export default SupportPDFReport;
